import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import cond from 'lodash/cond';

import { SuiInfiniteScroll, SuiRadioGroup, SuiRadioBehavior, SuiRadioDefaultUI } from '@splashthat/sui';
import { colorStorm } from 'common/styles/palette';
import { colorAppBorder, colorAppShadedBackground } from 'common/styles/colors';
import CubeLoaderOverlay from 'common/components/CubeLoaderOverlay';
import { MODAL_SIZE } from 'common/components/CubeLoader';

import ScaledFormsListOption from './ScaledFormListOption';
import { fetchLibraryFormList } from '../../epics/formLibraryEffects';
import { getFormLibraryList, getFormLibraryPage, getPageLimitReached } from '../../selectors/formLibrary';
import { setFormLibraryPage } from '../../actions/formLibrary';
import { getFormId } from '../../selectors';
import { FETCHING, APPLYING, DEFAULT, SEARCHING } from '../../reducers/formLibrary';

const RadioGroup = styled(SuiRadioGroup)`
    .SuiRadio--checked,
    .SuiRadio--unchecked {
        width: 100%;
    }
`;

const ScaledFormOptionContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: 5rem;
    padding: 0 1rem;
    padding: 0 1rem;
    border-bottom: 1px solid ${colorAppBorder};
`;

const NoFormsInLibContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    place-content: center;
    height: 100%;
    color: ${colorStorm};
`;

const MessageContainer = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
`;

const NoSearchMessage = styled.p`
    margin: 0 auto;
`;

export const NO_FORMS_IN_LIB_MESSAGE = "You don't have any forms in your library right now.";
export const NO_FORM_IN_LIB_INSTRUCTION_MESSAGE = 'You can add them with the "Add to Library" button.';
export const NO_SEARCH_RESULTS_MESSAGE = "We couldn't find any results. Try adjusting your search.";

const COMPONENT_NAME = 'ScaledFormsList';

const ScaledFormsList = ({ status, setSelectedForm, searchTerm, selectedForm }) => {
    const dispatch = useDispatch();
    const formList = useSelector(getFormLibraryList);
    const page = useSelector(getFormLibraryPage);
    const pageLimitReached = useSelector(getPageLimitReached);
    const formId = useSelector(getFormId);

    useEffect(() => {
        // Initial fetch
        if ((formList || []).length === 0) {
            dispatch(fetchLibraryFormList(page));
        }
    }, []);

    const handleHitBottom = () => {
        if (status !== FETCHING && !pageLimitReached) {
            const newPage = page + 1;

            dispatch(setFormLibraryPage(newPage));
            dispatch(fetchLibraryFormList(newPage));
        }
    };

    const handleChange = (event, value = event.target.value) => {
        setSelectedForm(value);
    };

    const renderLoader = () => (
        <CubeLoaderOverlay
            loading={status === FETCHING || status === APPLYING || status === SEARCHING}
            backgroundColor={colorAppShadedBackground}
            size={MODAL_SIZE}
            delay={0}
            data-qa={`${COMPONENT_NAME}__loader`}
        />
    );

    const renderNoFormsInLibrary = () => (
        <NoFormsInLibContainer>
            <p>{NO_FORMS_IN_LIB_MESSAGE}</p>
            <p>{NO_FORM_IN_LIB_INSTRUCTION_MESSAGE}</p>
        </NoFormsInLibContainer>
    );

    const renderNoSearchResults = () => (
        <MessageContainer data-qa={`${COMPONENT_NAME}__empty-state`}>
            <NoSearchMessage>{NO_SEARCH_RESULTS_MESSAGE}</NoSearchMessage>
        </MessageContainer>
    );

    const renderFormLibrary = () => (
        <SuiInfiniteScroll showLoader={status === FETCHING} onHitBottom={handleHitBottom}>
            <RadioGroup onChange={handleChange}>
                {formList.map((form) => (
                    <SuiRadioBehavior key={form.id} value={form.id} disabled={formId === `${form.formId}`}>
                        {(radioProps) => (
                            <ScaledFormOptionContainer data-qa={`${COMPONENT_NAME}__option-${form.id}`}>
                                <SuiRadioDefaultUI {...radioProps} isChecked={selectedForm === form.id} />
                                <ScaledFormsListOption {...form} />
                            </ScaledFormOptionContainer>
                        )}
                    </SuiRadioBehavior>
                ))}
            </RadioGroup>
        </SuiInfiniteScroll>
    );

    const showLoader = () =>
        ((formList || []).length === 0 && status === FETCHING) || status === APPLYING || status === SEARCHING;
    const showNoFormsInLib = () => (formList || []).length === 0 && status === DEFAULT && !searchTerm;
    const showNoSearchResults = () => (formList || []).length === 0 && status === DEFAULT && searchTerm;

    return cond([
        [showLoader, renderLoader],
        [showNoFormsInLib, renderNoFormsInLibrary],
        [showNoSearchResults, renderNoSearchResults],
        [() => true, renderFormLibrary],
    ])();
};

ScaledFormsList.propTypes = {
    setSelectedForm: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired,
    status: PropTypes.string,
    selectedForm: PropTypes.number,
};

ScaledFormsList.defaultProps = {
    status: DEFAULT,
    selectedForm: null,
};

export default ScaledFormsList;
